import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, Modal, TouchableOpacity, ActivityIndicator, Platform } from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { colores, fuentes } from '../../styles/variablesCss';
import { BACKEND_URL } from '../../Config/Const';
import { useUser } from '../../context/UserContext';
import { formatPrecio } from '../../utils/formatPrice';

const Bono = ({ visible, onClose, bono }) => {
  const [loading, setLoading] = useState(false);
  const [bonoData, setBonoData] = useState(null);
  const [error, setError] = useState(null);
  const { mail, codUsu } = useUser();

  useEffect(() => {
    if (visible) {
      fetchBonoData();
    }
  }, [visible, bono]);

  const fetchBonoData = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = `${BACKEND_URL}/rest/fin/bono?mail=${mail}&codUsu=${codUsu}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      //console.log('📡 Respuesta de API:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error('Error al obtener los datos del bono');
      }

      const data = await response.json();
      //console.log('✅ Datos recibidos:', data);
      
      // Extraer el saldo del bono de la estructura dsResultado
      let saldoBono = 0;
      if (data.dsResultado && data.dsResultado.bonoEmpleado && data.dsResultado.bonoEmpleado.length > 0) {
        saldoBono = data.dsResultado.bonoEmpleado[0].saldoBono || 0;
      }
      
      setBonoData({ saldoBono, rawData: data });
    } catch (err) {
      //console.error('❌ Error fetching bono:', err);
      setError(err.message || 'Error al cargar los datos del bono');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContainer}>
          {/* Header del modal */}
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Bonos Disponibles</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <MaterialCommunityIcons name="close" size={24} color={colores.grisOscuro} />
            </TouchableOpacity>
          </View>

          {/* Contenido del modal */}
          <View style={styles.modalContent}>
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colores.rojo} />
                <Text style={styles.loadingText}>Cargando bonos...</Text>
              </View>
            ) : error ? (
              <View style={styles.errorContainer}>
                <MaterialCommunityIcons name="alert-circle" size={48} color={colores.rojo} />
                <Text style={styles.errorText}>{error}</Text>
                <TouchableOpacity style={styles.retryButton} onPress={fetchBonoData}>
                  <Text style={styles.retryButtonText}>Reintentar</Text>
                </TouchableOpacity>
              </View>
            ) : bonoData ? (
              <View style={styles.bonoDataContainer}>
                <View style={styles.bonoIconContainer}>
                  <MaterialCommunityIcons name="ticket-percent" size={64} color={colores.rojo} />
                </View>
                
                <Text style={styles.bonoLabel}>Saldo de Bono Disponible</Text>
                <Text style={styles.bonoTotal}>$ {formatPrecio(bonoData.saldoBono)}</Text>
                
                <View style={styles.preguntaContainer}>
                  <Text style={styles.preguntaText}>¿Desea usar su bono para este pedido?</Text>
                </View>
              </View>
            ) : (
              <View style={styles.noDataContainer}>
                <MaterialCommunityIcons name="ticket-outline" size={48} color={colores.grisClaro} />
                <Text style={styles.noDataText}>No hay información de bonos disponible</Text>
              </View>
            )}
          </View>

          {/* Footer del modal */}
          <View style={styles.modalFooter}>
            {bonoData && !loading && !error ? (
              <View style={styles.botonesContainer}>
                <TouchableOpacity 
                  style={styles.botonSi} 
                  onPress={() => {
                   // console.log('✅ Usuario acepta usar bono, saldo:', bonoData.saldoBono);
                    onClose({ usarBono: true, saldoBono: bonoData.saldoBono }); // Pasar objeto con datos
                  }}
                >
                  <Text style={styles.botonSiText}>Sí, usar bono</Text>
                </TouchableOpacity>
                <TouchableOpacity 
                  style={styles.botonNo} 
                  onPress={() => {
                  //  console.log('❌ Usuario rechaza usar bono');
                    onClose({ usarBono: false, saldoBono: 0 }); // Pasar objeto indicando rechazo
                  }}
                >
                  <Text style={styles.botonNoText}>No, continuar sin bono</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity style={styles.cerrarButton} onPress={() => onClose({ usarBono: false, saldoBono: 0 })}>
                <Text style={styles.cerrarButtonText}>Cerrar</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: colores.blanco,
    borderRadius: 12,
    width: '100%',
    maxWidth: 500,
    maxHeight: '80%',
    ...Platform.select({
      web: {
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
        elevation: 5,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: colores.grisClaro,
  },
  modalTitle: {
    fontSize: 20,
    fontFamily: fuentes.bold,
    color: colores.textoPrincipal,
  },
  closeButton: {
    padding: 4,
  },
  modalContent: {
    padding: 20,
    minHeight: 200,
  },
  loadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 14,
    color: colores.grisOscuro,
    fontFamily: fuentes.regular,
  },
  errorContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  errorText: {
    marginTop: 12,
    fontSize: 14,
    color: colores.rojo,
    fontFamily: fuentes.regular,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 20,
    backgroundColor: colores.rojo,
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 6,
  },
  retryButtonText: {
    color: colores.blanco,
    fontSize: 14,
    fontFamily: fuentes.semiBold,
  },
  bonoDataContainer: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  bonoIconContainer: {
    marginBottom: 20,
  },
  bonoLabel: {
    fontSize: 16,
    color: colores.grisOscuro,
    fontFamily: fuentes.regular,
    marginBottom: 8,
  },
  bonoTotal: {
    fontSize: 36,
    color: colores.rojo,
    fontFamily: fuentes.bold,
    marginBottom: 20,
  },
  detalleContainer: {
    width: '100%',
    backgroundColor: colores.fondoClaro,
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
  },
  detalleTitle: {
    fontSize: 14,
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal,
    marginBottom: 8,
  },
  detalleText: {
    fontSize: 14,
    fontFamily: fuentes.regular,
    color: colores.grisOscuro,
    lineHeight: 20,
  },
  mensajeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#E3F2FD',
    padding: 12,
    borderRadius: 8,
    width: '100%',
  },
  mensajeText: {
    fontSize: 13,
    fontFamily: fuentes.regular,
    color: colores.azul,
    marginLeft: 8,
    flex: 1,
  },
  noDataContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  noDataText: {
    marginTop: 12,
    fontSize: 14,
    color: colores.grisClaro,
    fontFamily: fuentes.regular,
    textAlign: 'center',
  },
  modalFooter: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: colores.grisClaro,
  },
  preguntaContainer: {
    marginTop: 20,
    paddingHorizontal: 20,
  },
  preguntaText: {
    fontSize: 16,
    fontFamily: fuentes.semiBold,
    color: colores.textoPrincipal,
    textAlign: 'center',
    lineHeight: 24,
  },
  botonesContainer: {
    gap: 12,
  },
  botonSi: {
    backgroundColor: colores.rojo,
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: 'center',
    marginBottom: 8,
  },
  botonSiText: {
    color: colores.blanco,
    fontSize: 16,
    fontFamily: fuentes.bold,
  },
  botonNo: {
    backgroundColor: colores.blanco,
    paddingVertical: 14,
    borderRadius: 6,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colores.grisOscuro,
  },
  botonNoText: {
    color: colores.grisOscuro,
    fontSize: 16,
    fontFamily: fuentes.semiBold,
  },
  cerrarButton: {
    backgroundColor: colores.grisOscuro,
    paddingVertical: 12,
    borderRadius: 6,
    alignItems: 'center',
  },
  cerrarButtonText: {
    color: colores.blanco,
    fontSize: 16,
    fontFamily: fuentes.semiBold,
  },
});

export default Bono;
